import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  LabelList,
  ReferenceLine,
} from 'recharts'
import { Box, Typography } from '@mui/material'
import { useStore } from '~/state/useStore'
import type { WalkTimelineRow } from '~/api/endpoints/walks'
import { pivot as pivotTimeline } from './TimelineChart'
import {
  partition,
  computePanelAggregates,
  computeSeriesSums,
  computeGrowth,
  type Group,
} from './dataPipeline'
import { formatAxisTick, formatCompactNumber } from '~/util/numbers'
import { toLongDate } from '~/util/date'
import { CHROME, MESSAGES } from './chrome/chartChrome'
import {
  AXIS_TICK,
  GRID_STROKE,
  TOOLTIP_NUM_STYLE,
  GROWTH_NEUTRAL,
  ZERO_LINE_STROKE,
} from './chrome/chartStyle'
import { growthDisplay } from './chrome/growthDisplay'

export interface PeriodBar {
  label: string
  total: number
  growth: number | null
}

const TARGET_BARS = 12

export function formatPeriodLabel(time?: string | number): string {
  const s = String(time ?? '')
  if (!s) return ''
  try {
    return toLongDate(s)
  } catch {
    return s
  }
}

/** Row 24: range-form panel label — first AND last bucket date (bucket STARTS; under daily
 *  governance that is day-exact). Kills the "divergent date window" misread of start-only
 *  labels vs the timeline's sampled axis ticks. */
export function formatPeriodRangeLabel(points: Array<{ time?: string | number }>): string {
  const first = formatPeriodLabel(points[0]?.time)
  const last = formatPeriodLabel(points[points.length - 1]?.time)
  if (!first) return ''
  return last && last !== first ? `${first}${CHROME.rangeJoiner}${last}` : first
}

/** FY v2: range-form panel label at fy grain — same first/last shape as formatPeriodRangeLabel
 *  but through an FY label formatter (buildFyTickFormatter output: label + partial marker). */
export function formatFyPeriodRangeLabel(
  points: Array<{ time?: string | number }>,
  labelOf: (v: string | number) => string,
): string {
  const first = points[0]?.time == null ? '' : labelOf(points[0].time)
  const last =
    points[points.length - 1]?.time == null ? '' : labelOf(points[points.length - 1]!.time!)
  if (!first) return ''
  return last && last !== first ? `${first}${CHROME.rangeJoiner}${last}` : first
}

/** Aggregate timeline rows into per-period totals + period-over-period growth.
 *  Reuses the timeline pivot + partition + computePanelAggregates + computeGrowth.
 *  targetBars controls how many bars to aim for (default TARGET_BARS = 12);
 *  the period size is computed automatically — independent of any external granularity. */
export function toPeriodBars(
  rows: WalkTimelineRow[],
  targetBars: number = TARGET_BARS,
  labelFor: (time: string | number) => string = formatPeriodLabel,
): PeriodBar[] {
  const { points, seriesKeys } = pivotTimeline(rows)
  if (points.length === 0) return []
  const groups: Group[] = seriesKeys.map((key) => ({
    key,
    label: key,
    color: { hex: '#000000', opacity: 1 },
  }))
  const periodSize = Math.max(1, Math.ceil(points.length / Math.max(1, targetBars)))
  const panels = partition(points, periodSize)
  return panels.map((panel, i) => {
    const { sum } = computePanelAggregates(panel, groups)
    const prevSum = i > 0 ? computePanelAggregates(panels[i - 1] ?? [], groups).sum : 0
    return {
      label: labelFor(panel[0]?.time ?? ''),
      total: sum,
      growth: i === 0 ? null : computeGrowth(prevSum, sum),
    }
  })
}

export interface PeriodSeriesPoint {
  label: string
  [seriesKey: string]: string | number | undefined
}

/** Non-additive counterpart of toPeriodBars: same pivot/partition skeleton, but sums PER series
 *  key (computeSeriesSums) instead of collapsing series into one cross-series total
 *  (computePanelAggregates) — used when the strip's own additivity verdict says the total is
 *  unvouched (heterogeneous value_types, non-stackable group-by) and grouped per-VT bars are
 *  the honest rendering instead of a summed Σ. */
export function toPeriodSeriesPoints(
  rows: WalkTimelineRow[],
  targetBars: number = TARGET_BARS,
  labelFor: (time: string | number) => string = formatPeriodLabel,
): { points: PeriodSeriesPoint[]; seriesKeys: string[] } {
  const { points: rawPoints, seriesKeys } = pivotTimeline(rows)
  if (rawPoints.length === 0) return { points: [], seriesKeys: [] }
  const groups: Group[] = seriesKeys.map((key) => ({
    key,
    label: key,
    color: { hex: '#000000', opacity: 1 },
  }))
  const periodSize = Math.max(1, Math.ceil(rawPoints.length / Math.max(1, targetBars)))
  const panels = partition(rawPoints, periodSize)
  const points = panels.map((panel) => {
    const sums = computeSeriesSums(panel, groups)
    const point: PeriodSeriesPoint = { label: labelFor(panel[0]?.time ?? '') }
    for (const g of groups) point[g.key] = sums.get(g.key)
    return point
  })
  return { points, seriesKeys }
}

interface Props {
  rows?: WalkTimelineRow[]
  bars?: PeriodBar[]
  /** FY v2: label override for period labels (strip replay at fy passes an FY formatter).
   *  Default: formatPeriodLabel (long dates), unchanged. */
  labelFor?: (time: string | number) => string
  /** Chart-semantics doctrine (spec 2026-08-03 §1): whether the strip's cross-series total is
   *  vouched for. Default true — standalone renders (no caller opinion) keep summing. `false`
   *  switches to grouped per-value-type bars (toPeriodSeriesPoints) with NO summed total and NO
   *  GrowthLabel (per-series growth labels would be label soup — tooltip carries per-series
   *  values instead). */
  additive?: boolean
}

/** Wave-5 §3 (D4): in-chart growth labels ride the SAME mapping as GrowthChip
 *  (growthDisplay) — one growth vocabulary per page. Custom content (not a formatter)
 *  because LabelList's formatter cannot color per-datum; Recharts hands custom content
 *  the BAR rect, so the renderer centers itself and sits 6px above the bar top. */
export function GrowthLabel(props: {
  x?: number | string
  y?: number | string
  width?: number | string
  height?: number | string
  value?: number | string | null
  /** Sign-aware placement (Task 11): a negative-total bar draws DOWN from y=0, so its label
   *  sits BELOW the bar's end (y + height + 12) instead of above the bar top (y - 6) — the
   *  fixed "above the top" placement would land the label ON the zero line / inside the
   *  positive zone for a negative bar. Default false/undefined: unchanged 'above' placement. */
  below?: boolean
}) {
  const { x, y, width, value, below } = props
  if (typeof value !== 'number') return null
  if (x == null || y == null || width == null) return null
  const d = growthDisplay(value)
  if (d === null) return null
  const labelY = below ? Number(y) + Number(props.height ?? 0) + 12 : Number(y) - 6
  return (
    <text
      x={Number(x) + Number(width) / 2}
      y={labelY}
      textAnchor="middle"
      fontSize={11}
      fill={d.color ?? GROWTH_NEUTRAL}
      style={{ fontVariantNumeric: 'tabular-nums' }}
    >
      {d.text}
    </text>
  )
}

function NoDataForFilters() {
  return (
    <Box sx={{ p: 4, textAlign: 'center' }}>
      <Typography color="text.secondary">{MESSAGES.noDataForFilters}</Typography>
    </Box>
  )
}

export function SummaryBarStrip(props: Props) {
  const palette = useStore((s) => s.personal.chipPalette)

  // Chart-semantics doctrine (spec 2026-08-03 §1): a caller-declared additive:false verdict
  // means the cross-series total is unvouched — render one grouped bar series PER value type
  // instead of a summed Σ. No GrowthLabel here: period-over-period growth of an unvouched sum
  // is exactly the number this mode refuses to compute; per-series values ride the tooltip.
  if (props.additive === false) {
    const { points, seriesKeys } = toPeriodSeriesPoints(
      props.rows ?? [],
      TARGET_BARS,
      props.labelFor,
    )
    if (points.length === 0) return <NoDataForFilters />
    return (
      <Box sx={{ width: '100%', height: 220 }}>
        <ResponsiveContainer>
          <BarChart data={points} margin={{ top: 20, left: 24, right: 16, bottom: 8 }}>
            <CartesianGrid stroke={GRID_STROKE} vertical={false} />
            <XAxis dataKey="label" tick={AXIS_TICK} />
            <YAxis tickFormatter={formatAxisTick} tick={AXIS_TICK} />
            <Tooltip
              formatter={(value) => formatCompactNumber(value as number)}
              contentStyle={TOOLTIP_NUM_STYLE}
            />
            {seriesKeys.map((k, i) => (
              <Bar key={k} dataKey={k} name={k} fill={palette[i % palette.length] ?? '#5b9cff'} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Box>
    )
  }

  const bars = props.bars ?? toPeriodBars(props.rows ?? [], TARGET_BARS, props.labelFor)
  const color = palette[0] ?? '#5b9cff'

  if (bars.length === 0) return <NoDataForFilters />

  return (
    <Box sx={{ width: '100%', height: 220 }}>
      <ResponsiveContainer>
        <BarChart data={bars} margin={{ top: 20, left: 24, right: 16, bottom: 8 }}>
          <CartesianGrid stroke={GRID_STROKE} vertical={false} />
          {bars.some((b) => b.total < 0) ? (
            <ReferenceLine
              y={0}
              stroke={ZERO_LINE_STROKE}
              strokeWidth={2}
              label={{
                value: '0',
                position: 'insideBottomLeft',
                fontSize: 10,
                fill: ZERO_LINE_STROKE,
              }}
            />
          ) : null}
          <XAxis dataKey="label" tick={AXIS_TICK} />
          <YAxis tickFormatter={formatAxisTick} tick={AXIS_TICK} />
          <Tooltip
            formatter={(value) => formatCompactNumber(value as number)}
            contentStyle={TOOLTIP_NUM_STYLE}
          />
          <Bar dataKey="total" name="Σ" fill={color}>
            <LabelList
              dataKey="growth"
              content={(p) => <GrowthLabel {...p} below={(bars[p.index ?? 0]?.total ?? 0) < 0} />}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  )
}
